import { Fragment, memo, useEffect, useRef } from "react";

import styled from "styled-components";

import { IGroupByListItem } from "models";

import CategoryListItem from "./CategoryListItem";

interface CategoryListProps {
  readonly categoryList: Maybe<IGroupByListItem[]>;
  readonly selectedCategoryName?: Maybe<string>;
  readonly isFiltering?: boolean;
  readonly hoveredFieldGroup?: Maybe<string>;
  readonly active?: boolean;
  readonly focusedIndex?: Maybe<number>;
  readonly isOpen?: boolean;
  readonly onItemRef?: (index: number, el: Maybe<HTMLElement>) => void;
  readonly onCategoryClick?: (category: IGroupByListItem) => void;
}

const MemoizedItem = memo(CategoryListItem);

function GroupCategoryList({
  categoryList,
  selectedCategoryName = null,
  isFiltering = false,
  hoveredFieldGroup = null,
  active = false,
  focusedIndex = null,
  isOpen,
  onItemRef,
  onCategoryClick
}: CategoryListProps) {
  const shouldScrollRef = useRef(true);
  const selectedElRef = useRef<HTMLElement | null>(null);

  const scrollToSelected = (el: HTMLElement) => {
    // Defer to let popover/container finish layout before measuring scroll position
    requestAnimationFrame(() => {
      el.scrollIntoView({ block: "nearest" });
    });
  };

  // Scroll to the selected category when it changes or the container becomes visible
  useEffect(() => {
    shouldScrollRef.current = true;
    if (selectedElRef.current) {
      scrollToSelected(selectedElRef.current);
      shouldScrollRef.current = false;
    }
  }, [selectedCategoryName, isOpen]);

  // do not render if category-list is not initialized
  if (!categoryList?.length) return null;

  let currentSection = "Primary";

  return (
    <Wrapper>
      <ScrollableContainer>
        {categoryList.map((item, index) => {
          let useDivider = false;
          if (currentSection !== item.section) {
            useDivider = true;
            currentSection = item.section;
          }
          const isActive = active && focusedIndex === index;
          const isSelected =
            (!isFiltering && item.name === selectedCategoryName) ||
            (isFiltering && item.name === hoveredFieldGroup);

          return (
            <Fragment key={item.name}>
              {useDivider && <Divider />}
              <MemoizedItem
                value={item}
                isSelected={isSelected}
                active={isActive}
                itemRef={(el) => {
                  if (isSelected && !isFiltering) {
                    selectedElRef.current = el;
                    if (el && shouldScrollRef.current) {
                      scrollToSelected(el);
                      shouldScrollRef.current = false;
                    }
                  }
                  onItemRef?.(index, el);
                }}
                onCategoryClick={onCategoryClick}
              />
            </Fragment>
          );
        })}
      </ScrollableContainer>
    </Wrapper>
  );
}

export default memo(GroupCategoryList);

// Keep the white off the scroller: in software compositing it blanks the map (EVA-428).
const Wrapper = styled.div`
  min-width: 184px;
  display: grid;
  background-color: var(--color-white);
  overflow: hidden;
`;

const ScrollableContainer = styled.div`
  display: grid;
  align-content: start;
  padding: var(--space-2) 0;
  overflow: hidden overlay;
`;

const Divider = styled.div`
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  margin-top: 10px;
  margin-bottom: 10px;
`;
